/* eslint-disable import/order */
import React, { useEffect, useCallback, useMemo, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { Grid, GridToolbar, GridColumn as Column } from '@progress/kendo-react-grid';
import FilterPanel from '@components/FilterPanel';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import SearchPanel from '@components/SearchPanel';
import InspectionAssistantModal from '@features/InspectionAssistantModal';
import api from '@utils/axios';
import { KendoLocalizationProvider } from '@contexts/kendo-localization-context';
import I18n from '@utils/i18n';
import { datatableInspectionsPath } from 'routes';
import { saveCacheToLocalStorage } from '@helpers/localStorage';
import kendoDatatableParams from '@helpers/kendoDatatableParams';
import ColumnMenu from '../nested/ColumnMenu';
import LegacyMassActions from './LegacyMassActions';
import ColumnMenuContext from '../nested/ColumnMenuContext';
import '../index.css';
import getFiltersList from '../helpers/filtersList';
import { getInitialFilters, getInitialData } from '../helpers/initialData';
import { calculateColumnWidths } from '../helpers/columnWidth';
import massActionQueryParams from '../helpers/massActionQueryParams';
import { usePublishListContext } from '@features/AIChatWidget/hooks/usePublishListContext';
import { ALL_COLUMNS, ALL_COLUMNS_DATA, CACHE_KEY } from '../constants';
import { actionTypes, reducer } from '../reducer';

import { TimeZoneContextProvider } from '../../../contexts/timezone-context.tsx';
import CustomScrollbar from '../../../components/CustomScrollbar/CustomScrollbar';

function LegacyInspectionsIndex({
  frequenciesForSelect,
  inspectionTypesForSelect,
  statusesForSelect,
  territoriesForSelect,
  subcontractorsForSelect,
  optionalFilters,
  optionalMassActions,
  techniciansForSelect,
  assetsForSelect,
  buildingId,
  inspectionSeriesId,
  accountId,
  cacheKey,
  enableMoveSeries,
  timeZone,
  iimEnabled
}) {
  const [state, dispatch] = useReducer(
    reducer,
    { cacheKey, buildingId, inspectionSeriesId, accountId, enableMoveSeries, iimEnabled },
    getInitialData
  );
  const [successMessage, setSuccessMessage] = useState('');

  const { page, sort, filters, columns, selectAll, data, totalCount, loading, search } = state;

  // Publish live grid state to the AI chat widget (page context overlay).
  usePublishListContext({ page, sort, filters, totalCount, data, search });

  useEffect(() => {
    const moveSeriesButton = document.getElementById('move-series');
    const moveSeriesWrapper = document.getElementById('move-series-wrapper');

    if (moveSeriesButton) {
      moveSeriesButton.disabled = !state.enableMoveSeries;
    }
    if (moveSeriesWrapper) {
      moveSeriesWrapper.title = state.enableMoveSeries
        ? ''
        : I18n.t('inspection_series.show.inspection_series_push_series_modal.disabled_tooltip');
    }
  }, [state.enableMoveSeries]);

  useEffect(() => {
    saveCacheToLocalStorage(cacheKey, { page, sort, filters, columns });
  }, [page, sort, filters, columns]);

  useEffect(() => {
    const { asset, ...valuesFiltered } = filters.values;
    dispatch({ type: actionTypes.LOADING_CHANGED, loading: true });
    api
      .get(datatableInspectionsPath(), {
        params: kendoDatatableParams({
          page,
          sort,
          filters: { ...filters, values: valuesFiltered },
          search,
          optionalParams: asset && Array.isArray(asset) && { asset: asset.join(',') }
        })
      })
      .then(({ data: inspectionsData }) => {
        dispatch({ type: actionTypes.DATA_LOADED, data: inspectionsData });
      })
      .finally(() => {
        dispatch({ type: actionTypes.LOADING_CHANGED, loading: false });
      });
  }, [page, sort, filters, search]);

  const filtersList = useMemo(
    () =>
      getFiltersList({
        filters,
        dispatch,
        optionalFilters,
        frequenciesForSelect,
        inspectionTypesForSelect,
        territoriesForSelect,
        subcontractorsForSelect,
        assetsForSelect,
        buildingId,
        inspectionSeriesId,
        accountId
      }),
    [filters]
  );

  const debouncedSearchChange = useMemo(
    () =>
      debounce((value) => {
        dispatch({ type: actionTypes.SEARCH_CHANGED, search: value });
      }, 300),
    []
  );

  const onSearchChange = (value) => {
    debouncedSearchChange(value);
  };

  const onColumnsChange = useCallback(
    (cols) => dispatch({ type: actionTypes.COLUMNS_CHANGED, columns: cols.map((col) => col.id) }),
    []
  );

  const onResetFilters = useCallback(() => {
    dispatch({
      type: actionTypes.FILTER_CHANGED,
      filters: getInitialFilters(buildingId, inspectionSeriesId, accountId)
    });
  }, []);

  const onResetCache = () => {
    if (!window.confirm(I18n.t('generic.are_you_sure'))) return;

    localStorage.removeItem(cacheKey);
    window.location.reload();
  };

  const onSelectionChange = useCallback((event) => {
    dispatch({ type: actionTypes.SELECTION_CHANGED, id: event.dataItem.id });
  }, []);

  const onHeaderSelectionChange = useCallback((event) => {
    dispatch({ type: actionTypes.HEADER_SELECTION_CHANGED, checked: event.syntheticEvent.target.checked });
  }, []);

  const onSelectAll = useCallback(() => {
    dispatch({ type: actionTypes.ALL_SELECTED });
  }, []);

  const onDataChanged = (message, updatedData) => {
    setSuccessMessage(message);

    dispatch({ type: actionTypes.DATA_CHANGED, data: updatedData });
  };

  const headerSelectionValue = selectAll || (totalCount > 0 && data.every((inspection) => inspection.selected));

  const availableColumns = useMemo(
    () => (iimEnabled ? ALL_COLUMNS : ALL_COLUMNS.filter((col) => col !== 'unresolvedIssues')),
    [iimEnabled]
  );

  const allMenuColumns = useMemo(() => availableColumns.map((column) => ALL_COLUMNS_DATA[column]), [availableColumns]);
  const selectedMenuColumns = useMemo(
    () => columns.filter((col) => availableColumns.includes(col)).map((column) => ALL_COLUMNS_DATA[column]),
    [columns, availableColumns]
  );
  const visibleColumns = useMemo(
    () =>
      ['selected', ...columns.filter((col) => availableColumns.includes(col))].map(
        (column) => ALL_COLUMNS_DATA[column]
      ),
    [columns, availableColumns]
  );

  // Memoize calculated column widths - only recalculates when data changes
  const calculatedWidths = useMemo(() => {
    return calculateColumnWidths(data, visibleColumns);
  }, [data, visibleColumns]);

  const massActionParams = useMemo(() => massActionQueryParams({ filters, search }), [filters, search]);
  const columnContextValue = useMemo(
    () => ({
      onColumnsChange,
      columnsState: selectedMenuColumns,
      columns: allMenuColumns,
      statusesForSelect,
      dispatch
    }),
    [onColumnsChange, selectedMenuColumns, allMenuColumns, statusesForSelect, dispatch]
  );

  return (
    <TimeZoneContextProvider initialTimezone={timeZone}>
      <KendoLocalizationProvider>
        {successMessage && (
          <SuccessAlert
            message={successMessage}
            onClose={() => {
              setSuccessMessage('');
            }}
          />
        )}
        <ColumnMenuContext.Provider value={columnContextValue}>
          <FilterPanel onResetFilters={onResetFilters} onResetCache={onResetCache} filters={filtersList}>
            <div className="filters__group--search">
              <SearchPanel placeholder={I18n.t('filters.search')} onChange={onSearchChange} />
            </div>
          </FilterPanel>
          <hr className="workspace__split--sticky" />
          {loading && (
            <div className="workspace__section">
              <div className="qmb-loading--96" data-title={I18n.t('generic.loading')}>
                <svg role="img">
                  <use href="/map.svg#load-spinner" />
                </svg>
              </div>
            </div>
          )}
          {!loading && (
            <CustomScrollbar>
              <Grid
                data={data}
                total={totalCount}
                skip={page.skip}
                take={page.take}
                onPageChange={(event) => {
                  dispatch({ type: actionTypes.PAGE_CHANGED, page: event.page });
                }}
                pageable={{
                  type: 'input',
                  pageSizes: [25, 50, 100],
                  pageSizeValue: page.take
                }}
                pager={EnterKeyPager}
                sortable
                sort={sort}
                onSortChange={(event) => {
                  dispatch({ type: actionTypes.SORT_CHANGED, sort: event.sort });
                }}
                dataItemKey="id"
                selectedField="selected"
                onSelectionChange={onSelectionChange}
                onHeaderSelectionChange={onHeaderSelectionChange}
                className="qmb-grid--01">
                <GridToolbar>
                  <LegacyMassActions
                    state={state}
                    onSelectAll={onSelectAll}
                    statusesForSelect={statusesForSelect}
                    techniciansForSelect={techniciansForSelect}
                    inspectionTypesForSelect={inspectionTypesForSelect}
                    queryParams={massActionParams}
                    buildingId={buildingId}
                    optionalMassActions={optionalMassActions}
                    onDataChanged={onDataChanged}
                    inspectionSeriesId={inspectionSeriesId}
                  />
                </GridToolbar>

                {visibleColumns.map((column) => (
                  <Column
                    key={column.id}
                    field={column.field}
                    title={column.title}
                    columnMenu={ColumnMenu}
                    cell={column.cell}
                    headerCell={column.headerCell}
                    headerSelectionValue={headerSelectionValue}
                    sortable={column.sortable}
                    width={column.width || calculatedWidths[column.field] || 150}
                    className={column.className}
                    headerClassName={column.headerClassName}
                  />
                ))}
              </Grid>
            </CustomScrollbar>
          )}
        </ColumnMenuContext.Provider>
      </KendoLocalizationProvider>
      <InspectionAssistantModal />
    </TimeZoneContextProvider>
  );
}

function SuccessAlert({ message, onClose }) {
  return (
    <section className="workspace__section--alerts">
      <div className="qmb-alert --success">
        <i className="alert__visual" />
        <span id="success_alert_text">{message}</span>
        <button
          type="button"
          aria-label={I18n.t('generic.close')}
          className="qmb-control--icon--close fa-light fa-xmark"
          onClick={onClose}
        />
      </div>
    </section>
  );
}

SuccessAlert.propTypes = {
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

LegacyInspectionsIndex.propTypes = {
  frequenciesForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  inspectionTypesForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  statusesForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  territoriesForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  subcontractorsForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  optionalFilters: PropTypes.shape({
    subcontractor: PropTypes.bool,
    tce: PropTypes.bool,
    inspectionType: PropTypes.bool
  }),
  optionalMassActions: PropTypes.shape({
    pushToTCE: PropTypes.bool
  }),
  techniciansForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  assetsForSelect: PropTypes.arrayOf(PropTypes.object),
  buildingId: PropTypes.number,
  inspectionSeriesId: PropTypes.number,
  accountId: PropTypes.number,
  cacheKey: PropTypes.string,
  enableMoveSeries: PropTypes.bool,
  timeZone: PropTypes.string,
  iimEnabled: PropTypes.bool
};

LegacyInspectionsIndex.defaultProps = {
  territoriesForSelect: [],
  frequenciesForSelect: [],
  inspectionTypesForSelect: [],
  statusesForSelect: [],
  subcontractorsForSelect: [],
  optionalFilters: [],
  optionalMassActions: {},
  techniciansForSelect: [],
  assetsForSelect: [],
  buildingId: null,
  inspectionSeriesId: null,
  accountId: null,
  cacheKey: CACHE_KEY,
  enableMoveSeries: null,
  timeZone: null,
  iimEnabled: false
};

export default LegacyInspectionsIndex;
